"use client";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { map, filter as lodashFilter, orderBy } from "lodash";
import ShowService, { Show } from "@/app/_services/ShowService";
import { dayjsTz } from "@/app/_utils/dayjs";
import { formatShowLabel } from "@/app/_utils/showLabel";
import { deriveShowStatus, ShowStatus } from "./_cmp/showStatus";

export type ViewMode = "list" | "calendar";

const SEARCH_DEBOUNCE_MS = 300;

function parseShows(res: any) {
  return map(res, (it: any) => ({
    ...it,
    start: dayjsTz(it.start).format(),
    title: formatShowLabel(it.title, it.variant_name),
  }));
}

export function useShowsModel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Calendar month state, synced to ?date= for shareable/bookmarkable links.
  // Drives the month-scoped fetch for both List and Calendar views.
  const initialDate = searchParams.get("date");
  const initialMoment = initialDate ? dayjsTz(initialDate) : dayjsTz();
  const [calendarYear, setCalendarYear] = useState(initialMoment.year());
  const [calendarMonth, setCalendarMonth] = useState(initialMoment.month());

  const [shows, setShows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ShowStatus | "all">("all");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const isSearchActive = search.trim().length > 0;

  async function getShows() {
    setIsLoading(true);
    // Backend months are 1-12; calendarMonth (from dayjs) is 0-11.
    const res = await ShowService.getShow({ year: calendarYear, month: calendarMonth + 1 });
    setShows(parseShows(res));
    setIsLoading(false);
  }

  useEffect(() => {
    getShows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarYear, calendarMonth]);

  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      const res = await ShowService.getShow({ q });
      setSearchResults(parseShows(res));
      setIsSearching(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const activeShows = isSearchActive ? searchResults : shows;

  const showsWithStatus = useMemo(
    () => activeShows.map((show) => ({ ...show, status: deriveShowStatus(show) })),
    [activeShows]
  );

  const filteredShows = useMemo(() => {
    let result = showsWithStatus;
    if (statusFilter !== "all") {
      result = lodashFilter(result, (s) => s.status === statusFilter);
    }
    return orderBy(result, ["start"], ["desc"]);
  }, [showsWithStatus, statusFilter]);

  const kpis = useMemo(() => {
    const now = dayjsTz();
    const upcomingShows = shows.filter((s) => dayjsTz(s.start).isAfter(now)).length;
    const ticketsSold = shows.reduce((sum, s) => sum + (Number(s.sold) || 0), 0);
    const withCapacity = shows.filter((s) => Number(s.capacity) > 0);
    const avgSellThrough =
      withCapacity.length === 0
        ? 0
        : Math.round(
            (withCapacity.reduce(
              (sum, s) => sum + Number(s.sold) / Number(s.capacity),
              0
            ) /
              withCapacity.length) *
              100
          );
    const revenue = shows.reduce(
      (sum, s) => sum + (Number(s.sold) || 0) * (Number(s.ticket_price) || 0),
      0
    );
    const onSaleCount = showsWithStatus.filter((s) => s.status === "on-sale").length;
    return { upcomingShows, ticketsSold, avgSellThrough, revenue, onSaleCount };
  }, [shows, showsWithStatus]);

  function setCalendarMonthAndSync(year: number, month: number) {
    setCalendarYear(year);
    setCalendarMonth(month);
    const mid = dayjsTz(new Date(year, month, 15));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("date", mid.format("YYYY-MM-DD"));
        return next;
      },
      { replace: true }
    );
  }

  function goToDate(dateStr: string) {
    navigate(`/admin/shows/newshow?startDate=${dateStr}`);
  }

  return {
    shows,
    filteredShows,
    isLoading: isSearchActive ? isSearching : isLoading,
    isSearchActive,
    viewMode,
    setViewMode,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    kpis,
    calendarYear,
    calendarMonth,
    setCalendarMonth: setCalendarMonthAndSync,
    goToDate,
    refetch: getShows,
  };
}

export type { Show };
